import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  TrophyIcon, 
  CalendarIcon,
  MapPinIcon,
  UsersIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toProperCase } from '../utils/formatUtils';

/** Parse YYYY-MM-DD as end of that local calendar day (deadline day is inclusive). */
const getDeadlineEndOfDay = (deadlineStr) => {
  if (!deadlineStr) return new Date(0);
  const datePart = String(deadlineStr).split('T')[0].split(' ')[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    const [y, m, d] = parts;
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  }
  const fallback = new Date(deadlineStr);
  fallback.setHours(23, 59, 59, 999);
  return fallback;
};

/** Format a deadline date for display without timezone shift. */
const formatDeadlineDate = (deadlineStr) => {
  if (!deadlineStr) return '';
  const datePart = String(deadlineStr).split('T')[0].split(' ')[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    const [y, m, d] = parts;
    return new Date(y, m - 1, d).toLocaleDateString();
  }
  return new Date(deadlineStr).toLocaleDateString();
};

// Competition Card Component
const CompetitionCard = ({ competition, onRegister, existingRegistration }) => {
  const getRegistrationStatus = useCallback((competition) => {
    const deadline = getDeadlineEndOfDay(competition.registration_deadline);
    const now = new Date();
    
    if (now > deadline) {
      return { status: 'closed', message: 'Registration closed', color: 'text-red-600' };
    }
    
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 3) {
      return { status: 'urgent', message: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`, color: 'text-orange-600' };
    }
    
    return { status: 'open', message: `${daysLeft} days left`, color: 'text-green-600' };
  }, []);

  const regStatus = useMemo(() => getRegistrationStatus(competition), [competition, getRegistrationStatus]);
  const isRegistered = Boolean(existingRegistration);
  const canEdit = isRegistered && regStatus.status !== 'closed';

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-primary-500 transition-all duration-300 flex flex-col shadow-lg hover:shadow-xl">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <TrophyIcon className="h-8 w-8 text-primary-600 mr-3" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">{competition.name}</h3>
              <p className="text-primary-600 text-sm">{competition.location}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${regStatus.color}`}>
              {regStatus.message}
            </span>
            {isRegistered && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                Registered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-grow space-y-3">
        <div className="flex items-start">
          <CalendarIcon className="h-4 w-4 mr-2 mt-1 text-primary-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">Date</p>
            <p className="text-sm text-gray-600">
              {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="flex items-start">
          <MapPinIcon className="h-4 w-4 mr-2 mt-1 text-primary-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">Location</p>
            <p className="text-sm text-gray-600">{competition.location}</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <UsersIcon className="h-4 w-4 mr-2 mt-1 text-primary-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">Registrations</p>
            <p className="text-sm text-gray-600">{competition.registration_count || 0} registered</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <ClockIcon className="h-4 w-4 mr-2 mt-1 text-primary-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">Registration</p>
            <p className="text-sm text-gray-600">
              Closes: {formatDeadlineDate(competition.registration_deadline)}
            </p>
          </div>
        </div>

        {/* Events Preview */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Available Events:</h4>
          <div className="flex flex-wrap gap-1">
            {competition.events.slice(0, 3).map((event, index) => (
              <span key={index} className="bg-primary-100 text-primary-700 px-2 py-1 rounded text-xs">
                {toProperCase(event.event_code || '')}
              </span>
            ))}
            {competition.events.length > 3 && (
              <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                +{competition.events.length - 3} more
              </span>
            )}
          </div>
        </div>
        {isRegistered && existingRegistration?.events_registered?.length > 0 && (
          <div className="mb-2">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Your entries:</h4>
            <div className="flex flex-wrap gap-1">
              {existingRegistration.events_registered.map((event, index) => (
                <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                  {toProperCase(event.event_name || event.event_code || '')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-gray-200 mt-auto">
        <button
          onClick={() => onRegister(competition, existingRegistration)}
          disabled={regStatus.status === 'closed' && !canEdit}
          className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center ${
            regStatus.status === 'closed' && !canEdit
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : canEdit
                ? 'bg-oldham-gold hover:bg-oldham-gold/90 text-gray-900'
                : 'bg-primary-600 hover:bg-primary-700 text-white'
          }`}
        >
          <TrophyIcon className="w-4 h-4 mr-2" />
          {regStatus.status === 'closed' && !canEdit
            ? 'Registration Closed'
            : canEdit
              ? 'Edit Registration'
              : 'Register Now'}
        </button>
      </div>
    </div>
  );
};

// Event Selection Component
const EventSelection = ({ events, selectedEvents, onEventToggle, userGender, onGenderUpdate }) => {
  // Handle gender selection
  const handleGenderSelection = async (selectedGender) => {
    if (onGenderUpdate) {
      await onGenderUpdate(selectedGender);
    }
  };

  // Filter events by user's gender
  const filteredEvents = useMemo(() => {
    console.log('EventSelection - All events:', events);
    console.log('EventSelection - User gender:', userGender);
    
    if (!userGender) {
      console.log('No user gender set, showing all events');
      return events;
    }
    
    const filtered = events.filter(e => e.gender === userGender);
    console.log('Filtered events for gender', userGender, ':', filtered);
    console.log('Available genders in events:', [...new Set(events.map(e => e.gender))]);
    
    return filtered;
  }, [events, userGender]);

  const trackEvents = useMemo(() => filteredEvents.filter(e => e.type === 'track'), [filteredEvents]);
  const fieldEvents = useMemo(() => filteredEvents.filter(e => e.type === 'field'), [filteredEvents]);
  const otherEvents = useMemo(() => filteredEvents.filter(e => e.type !== 'track' && e.type !== 'field'), [filteredEvents]);

  const EventGroup = ({ title, eventList, type }) => {
    const currentCount = selectedEvents.filter(e => e.type === type).length;
    const maxCount = type === 'track' || type === 'field' ? 2 : Infinity;

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-medium text-gray-700">{title}</h4>
          <span className="text-sm text-gray-500">
            {currentCount}/{maxCount} selected
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {eventList.map((event, index) => {
            const isSelected = selectedEvents.some(e => e.event_code === event.event_code);
            const isDisabled = !isSelected && currentCount >= maxCount;
            const selectedEventData = selectedEvents.find(e => e.event_code === event.event_code);
            
            return (
              <div key={index} className={`p-3 border rounded-md ${
                isSelected ? 'bg-primary-50 border-primary-500' : 'hover:bg-gray-50'
              } ${isDisabled ? 'opacity-50' : ''}`}>
                <label className={`flex items-center cursor-pointer ${isDisabled ? 'cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={(e) => onEventToggle(event, e.target.checked)}
                    className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        event.type === 'track' ? 'bg-blue-100 text-blue-800' : 
                        event.type === 'field' ? 'bg-green-100 text-green-800' : 
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {event.type.toUpperCase()}
                      </span>
                      <span className="font-medium text-gray-900">{toProperCase(event.event_name || event.event_code || '')}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {toProperCase(event.event_code || '')} • ${event.entry_fee || '0'}
                    </div>
                    {event.divisions && event.divisions.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Available: {event.divisions.join(', ')}
                      </div>
                    )}
                  </div>
                </label>
                
                {/* Division Selection - Show when event is selected */}
                {isSelected && event.divisions && event.divisions.length > 1 && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Your Division (Age Group) *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {event.divisions.map((division) => (
                        <label key={division} className="flex items-center space-x-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name={`division-${event.event_code}`}
                            value={division}
                            checked={selectedEventData?.division === division}
                            onChange={(e) => {
                              onEventToggle({ ...event, division: e.target.value }, true);
                            }}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <span className="text-gray-700">{division}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h4 className="text-md font-medium text-gray-700 mb-4">Select Events</h4>
      
      {/* Gender selection if not set */}
      {!userGender && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <h4 className="text-sm font-medium text-blue-800 mb-3">Select Your Gender</h4>
          <p className="text-sm text-blue-700 mb-3">
            Please select your gender to see available events for registration.
          </p>
          <div className="flex gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="gender-selection"
                value="M"
                onChange={(e) => handleGenderSelection(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm font-medium text-blue-800">Male</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="gender-selection"
                value="F"
                onChange={(e) => handleGenderSelection(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm font-medium text-blue-800">Female</span>
            </label>
          </div>
        </div>
      )}

      {/* Gender info banner */}
      {userGender && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
          <p className="text-sm text-green-700">
            Showing {userGender === 'M' ? 'Male' : 'Female'} events based on your profile
          </p>
        </div>
      )}

      {/* No events message */}
      {userGender && filteredEvents.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
          <p className="text-sm text-yellow-700">
            No {userGender === 'M' ? 'Male' : 'Female'} events available for this competition. 
            All events may be available for both genders or the competition may not have events for your gender.
          </p>
        </div>
      )}
      
      {/* Event Limits Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Event Registration Rules:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Maximum 2 track events and 2 field events per competition</li>
          <li>• Contact admin for special permissions beyond limits</li>
          <li>• Current selection: {trackEvents.filter(e => selectedEvents.some(se => se.event_code === e.event_code)).length}/2 track, {fieldEvents.filter(e => selectedEvents.some(se => se.event_code === e.event_code)).length}/2 field</li>
        </ul>
      </div>

      {trackEvents.length > 0 && (
        <EventGroup title="Track Events" eventList={trackEvents} type="track" />
      )}
      
      {fieldEvents.length > 0 && (
        <EventGroup title="Field Events" eventList={fieldEvents} type="field" />
      )}
      
      {otherEvents.length > 0 && (
        <EventGroup title="Other Events" eventList={otherEvents} type="other" />
      )}
    </div>
  );
};

// Registration Form Component
const RegistrationForm = ({ 
  competition, 
  registrationData, 
  onDataChange, 
  onSubmit, 
  onCancel,
  userGender,
  onGenderUpdate,
  profileSeasonBests = {},
  profilePersonalBests = {},
  onSaveSeedAsSb,
  isEditing = false
}) => {
  const handleInputChange = useCallback((field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      onDataChange({
        ...registrationData,
        [parent]: {
          ...registrationData[parent],
          [child]: value
        }
      });
    } else {
      onDataChange({
        ...registrationData,
        [field]: value
      });
    }
  }, [registrationData, onDataChange]);

  const handleEventToggle = useCallback((event, checked) => {
    if (checked) {
      // Check if event already exists (for division updates)
      const existingEventIndex = registrationData.events.findIndex(e => e.event_code === event.event_code);
      
      if (existingEventIndex >= 0) {
        // Update existing event (e.g., division changed) - preserve seed_timing
        const updatedEvents = [...registrationData.events];
        const existing = updatedEvents[existingEventIndex];
        updatedEvents[existingEventIndex] = {
          event_code: event.event_code,
          event_name: event.event_name,
          type: event.type,
          gender: event.gender,
          division: event.division || (event.divisions && event.divisions[0]) || 'Opens',
          entry_fee: event.entry_fee || 0,
          seed_timing: existing.seed_timing ?? (profileSeasonBests[event.event_code] || profilePersonalBests[event.event_code] || '')
        };
        onDataChange({
          ...registrationData,
          events: updatedEvents
        });
      } else {
        // Add new event - check limits first
        const currentTrackEvents = registrationData.events.filter(e => e.type === 'track').length;
        const currentFieldEvents = registrationData.events.filter(e => e.type === 'field').length;
        
        if (event.type === 'track' && currentTrackEvents >= 2) {
          alert('Maximum of 2 track events allowed. Please remove a track event first or contact admin for special permissions.');
          return;
        }
        
        if (event.type === 'field' && currentFieldEvents >= 2) {
          alert('Maximum of 2 field events allowed. Please remove a field event first or contact admin for special permissions.');
          return;
        }

        // Add new event
        const seedFromProfile = profileSeasonBests[event.event_code] || profilePersonalBests[event.event_code] || '';
        onDataChange({
          ...registrationData,
          events: [...registrationData.events, {
            event_code: event.event_code,
            event_name: event.event_name,
            type: event.type,
            gender: event.gender,
            division: event.division || (event.divisions && event.divisions[0]) || 'Opens',
            entry_fee: event.entry_fee || 0,
            seed_timing: seedFromProfile
          }]
        });
      }
    } else {
      onDataChange({
        ...registrationData,
        events: registrationData.events.filter(e => e.event_code !== event.event_code)
      });
    }
  }, [registrationData, onDataChange, profileSeasonBests, profilePersonalBests]);

  const handleSeedTimingChange = useCallback((eventCode, value, eventName) => {
    const updated = registrationData.events.map(ev =>
      ev.event_code === eventCode ? { ...ev, seed_timing: value } : ev
    );
    onDataChange({ ...registrationData, events: updated });
  }, [registrationData, onDataChange]);

  const handleSeedBlurWithSavePrompt = useCallback((eventCode, value, eventName) => {
    const trimmed = (value || '').trim();
    if (!trimmed || !onSaveSeedAsSb) return;
    if (profileSeasonBests[eventCode] === trimmed) return;
    if (window.confirm(`Save ${trimmed} as your Season Best (SB) for ${eventName || eventCode} in your profile?`)) {
      onSaveSeedAsSb(eventCode, trimmed);
    }
  }, [onSaveSeedAsSb, profileSeasonBests]);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Event Selection */}
      <EventSelection 
        events={competition.events}
        selectedEvents={registrationData.events}
        onEventToggle={handleEventToggle}
        userGender={userGender}
        onGenderUpdate={onGenderUpdate}
      />

      {/* Seed timing per selected event */}
      {registrationData.events.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3">Seed timing (optional)</h4>
          <p className="text-sm text-gray-500 mb-3">Pre-filled from your profile PB/SB. You can edit and optionally save as SB.</p>
          <div className="space-y-3">
            {registrationData.events.map((ev) => (
              <div key={ev.event_code} className="flex items-center gap-4">
                <label className="flex-1 text-sm font-medium text-gray-700">{toProperCase(ev.event_name || ev.event_code || '')}</label>
                <input
                  type="text"
                  value={ev.seed_timing || ''}
                  onChange={(e) => handleSeedTimingChange(ev.event_code, e.target.value, ev.event_name)}
                  onBlur={(e) => handleSeedBlurWithSavePrompt(ev.event_code, e.target.value.trim(), ev.event_name)}
                  placeholder="e.g. 11.90"
                  className="flex-1 max-w-[120px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* IC Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700">IC Number (Last 4 digits) *</label>
        <input
          type="text"
          required
          maxLength="4"
          pattern="[0-9]{3}[A-Za-z]"
          value={registrationData.ic_number}
          onChange={(e) => handleInputChange('ic_number', e.target.value)}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
          placeholder="123A"
        />
        <p className="mt-1 text-sm text-gray-500">
          Enter the last 4 characters of your IC number (3 numbers + 1 letter, e.g., 123A)
        </p>
      </div>

      {/* Emergency Contact */}
      <div>
        <h4 className="text-md font-medium text-gray-700 mb-3">Emergency Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name</label>
            <input
              type="text"
              required
              value={registrationData.emergency_contact.name}
              onChange={(e) => handleInputChange('emergency_contact.name', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              type="tel"
              required
              value={registrationData.emergency_contact.phone}
              onChange={(e) => handleInputChange('emergency_contact.phone', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Coach Information */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Coach Name</label>
        <input
          type="text"
          required
          value={registrationData.coach_name}
          onChange={(e) => handleInputChange('coach_name', e.target.value)}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Team Information (Optional) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Team Code (Optional)</label>
          <input
            type="text"
            value={registrationData.team_code}
            onChange={(e) => handleInputChange('team_code', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Team Name (Optional)</label>
          <input
            type="text"
            value={registrationData.team_name}
            onChange={(e) => handleInputChange('team_name', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* PAR-Q Confirmation */}
      <div className="flex items-center">
        <input
          type="checkbox"
          required
          checked={registrationData.par_q}
          onChange={(e) => handleInputChange('par_q', e.target.checked)}
          className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label className="text-sm text-gray-700">
          I confirm that I have completed the PAR-Q (Physical Activity Readiness Questionnaire)
        </label>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          {isEditing ? 'Save Changes' : 'Register'}
        </button>
      </div>
    </form>
  );
};

// Registration Modal Component
const RegistrationModal = ({ 
  competition, 
  registrationData, 
  onDataChange, 
  onSubmit, 
  onClose,
  userGender,
  onGenderUpdate,
  profileSeasonBests,
  profilePersonalBests,
  onSaveSeedAsSb,
  isEditing = false
}) => {
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (registrationData.events.length === 0) {
      alert('Please select at least one event');
      return;
    }

    if (!registrationData.ic_number || registrationData.ic_number.length !== 4 || !/^[0-9]{3}[A-Za-z]$/.test(registrationData.ic_number)) {
      alert('Please provide your IC number (last 4 characters: 3 numbers + 1 letter, e.g., 123A)');
      return;
    }

    if (!registrationData.emergency_contact.name || !registrationData.emergency_contact.phone) {
      alert('Please provide emergency contact information');
      return;
    }

    if (!registrationData.coach_name) {
      alert('Please provide coach name');
      return;
    }

    if (!registrationData.par_q) {
      alert('Please confirm PAR-Q completion');
      return;
    }

    await onSubmit(registrationData);
  }, [registrationData, onSubmit]);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {isEditing ? `Edit registration for ${competition.name}` : `Register for ${competition.name}`}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <RegistrationForm
            competition={competition}
            registrationData={registrationData}
            onDataChange={onDataChange}
            onSubmit={handleSubmit}
            onCancel={onClose}
            userGender={userGender}
            onGenderUpdate={onGenderUpdate}
            profileSeasonBests={profileSeasonBests}
            profilePersonalBests={profilePersonalBests}
            onSaveSeedAsSb={onSaveSeedAsSb}
            isEditing={isEditing}
          />
        </div>
      </div>
    </div>
  );
};

// Main Component
const MemberCompetitions = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // State Management
  const [competitions, setCompetitions] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [isEditingRegistration, setIsEditingRegistration] = useState(false);
  const [userGender, setUserGender] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [registrationData, setRegistrationData] = useState({
    events: [],
    ic_number: '',
    emergency_contact: {
      name: '',
      phone: '',
      relationship: ''
    },
    coach_name: '',
    par_q: false,
    team_code: '',
    team_name: ''
  });
  const [profileSeasonBests, setProfileSeasonBests] = useState({});
  const [profilePersonalBests, setProfilePersonalBests] = useState({});

  // Data fetching functions
  const fetchCompetitions = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/competitions/list?status=open`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch competitions');
      }

      const data = await response.json();
      const competitionsData = data.competitions || [];
      
      // Fetch specifications for each event in each competition
      const enrichedCompetitions = await Promise.all(
        competitionsData.map(async (comp) => {
          const enrichedEvents = await Promise.all(
            comp.events.map(async (event) => {
              try {
                // Fetch specifications for this event
                const specsResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/event-specs/${event.event_code}`
                );
                
                if (specsResponse.ok) {
                  const specsData = await specsResponse.json();
                  return {
                    ...event,
                    specifications: specsData.specifications || {}
                  };
                }
              } catch (error) {
                console.error(`Error fetching specs for ${event.event_code}:`, error);
              }
              
              return event;
            })
          );
          
          return {
            ...comp,
            events: enrichedEvents
          };
        })
      );
      
      setCompetitions(enrichedCompetitions);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyRegistrations = useCallback(async () => {
    if (!currentUser) {
      setMyRegistrations({});
      return;
    }
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/competitions/my-registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) return;
      const data = await response.json();
      const byCompetitionId = {};
      (data.registrations || []).forEach((reg) => {
        const key = reg.id || reg.competition_id;
        if (key) byCompetitionId[key] = reg;
      });
      setMyRegistrations(byCompetitionId);
    } catch (error) {
      console.error('Error fetching my registrations:', error);
    }
  }, [currentUser]);

  const fetchUserPreferences = useCallback(async () => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/competitions/user-preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences) {
          setRegistrationData(prev => ({
            ...prev,
            emergency_contact: data.preferences.emergency_contact || prev.emergency_contact,
            coach_name: data.preferences.coach_name || prev.coach_name,
            team_code: data.preferences.team_code || prev.team_code,
            team_name: data.preferences.team_name || prev.team_name
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  }, [currentUser]);

  const fetchUserProfile = useCallback(async () => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.user || null);

        const competitionProfile = data.user.competition_profile || {};
        // Use flat current_season_bests for registration prepopulate (nested + flat fallback from backend)
        const seasonBestsForPrepopulate = data.user.current_season_bests || competitionProfile.season_bests || {};
        
        // Set user gender from profile
        setUserGender(data.user.gender || null);
        
        setProfilePersonalBests(competitionProfile.personal_bests || {});
        setProfileSeasonBests(seasonBestsForPrepopulate);
        
        setRegistrationData({
          events: [],
          ic_number: data.user.ic_number || '',
          emergency_contact: {
            name: competitionProfile.emergency_contact?.name || '',
            phone: competitionProfile.emergency_contact?.phone || '',
            relationship: competitionProfile.emergency_contact?.relationship || ''
          },
          coach_name: competitionProfile.coach_name || '',
          par_q: competitionProfile.par_q_completed || false,
          team_code: competitionProfile.team_code || 'OA',
          team_name: competitionProfile.team_name || 'Oldham Athletics'
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, [currentUser]);

  // Required profile fields before competition registration
  const requiredProfileFields = useMemo(() => {
    const p = userProfile || {};
    const cp = p.competition_profile || {};
    const ec = p.emergency_contact || {};
    const firstName = (p.first_name || '').toString().trim();
    const lastName = (p.last_name || '').toString().trim();
    const birthDate = (p.date_of_birth || p.birth_date || '').toString().trim();
    const contactNumber = (p.contact_number || '').toString().trim();
    const coachName = (p.coach_name || cp.coach_name || '').toString().trim();
    const nationality = (p.nationality || p.Nationality || '').toString().trim();
    const gender = (p.gender || '').toString().trim();
    const icNumber = (p.ic_number || '').toString().trim();
    const emergencyName = (ec.name || '').toString().trim();
    const emergencyPhone = (ec.phone || '').toString().trim();
    const hasEmergencyContact = Boolean(emergencyName && emergencyPhone);
    return {
      firstName, lastName, birthDate, contactNumber, coachName,
      nationality, gender, icNumber, hasEmergencyContact
    };
  }, [userProfile]);

  const isProfileCompleteForRegistration = useMemo(() => {
    const {
      firstName, lastName, birthDate, contactNumber, coachName,
      nationality, gender, icNumber, hasEmergencyContact
    } = requiredProfileFields;
    return Boolean(
      firstName && lastName && birthDate && contactNumber && coachName &&
      nationality && gender && icNumber && hasEmergencyContact
    );
  }, [requiredProfileFields]);

  // Event handlers
  const handleRegister = useCallback((competition, existingRegistration = null) => {
    if (userProfile && !isProfileCompleteForRegistration) {
      const missing = [];
      if (!requiredProfileFields.firstName) missing.push('First name');
      if (!requiredProfileFields.lastName) missing.push('Last name');
      if (!requiredProfileFields.birthDate) missing.push('Birth date');
      if (!requiredProfileFields.contactNumber) missing.push('Contact number');
      if (!requiredProfileFields.coachName) missing.push('Coach name');
      if (!requiredProfileFields.nationality) missing.push('Nationality');
      if (!requiredProfileFields.gender) missing.push('Gender');
      if (!requiredProfileFields.icNumber) missing.push('IC number');
      if (!requiredProfileFields.hasEmergencyContact) missing.push('Emergency contact (name and phone)');
      const msg = `Please complete your profile before registering for a competition. Missing: ${missing.join(', ')}. You will be redirected to your profile.`;
      alert(msg);
      navigate('/profile');
      return;
    }

    const editing = Boolean(existingRegistration);
    setIsEditingRegistration(editing);
    setSelectedCompetition(competition);

    if (editing) {
      const registeredEvents = (existingRegistration.events_registered || []).map((ev) => {
        const compEvent = (competition.events || []).find((e) => e.event_code === ev.event_code) || {};
        return {
          event_code: ev.event_code,
          event_name: ev.event_name || compEvent.event_name || ev.event_code,
          type: ev.type || compEvent.type || 'track',
          gender: ev.gender || compEvent.gender || userGender,
          division: ev.division || 'Opens',
          entry_fee: ev.entry_fee ?? compEvent.entry_fee ?? 0,
          seed_timing: ev.seed_timing || ''
        };
      });
      const emergency = existingRegistration.emergency_contact || {};
      setRegistrationData({
        events: registeredEvents,
        ic_number: existingRegistration.ic_number || '',
        emergency_contact: {
          name: emergency.name || '',
          phone: emergency.phone || '',
          relationship: emergency.relationship || ''
        },
        coach_name: existingRegistration.coach_name || '',
        par_q: Boolean(existingRegistration.par_q),
        team_code: existingRegistration.team_code || '',
        team_name: existingRegistration.team_name || ''
      });
      setShowRegistrationModal(true);
    } else {
      setShowRegistrationModal(true);
      fetchUserPreferences();
    }
  }, [fetchUserPreferences, userProfile, isProfileCompleteForRegistration, requiredProfileFields, navigate, userGender]);

  const handleSubmitRegistration = useCallback(async (data) => {
    try {
      const token = await currentUser.getIdToken();
      const method = isEditingRegistration ? 'PUT' : 'POST';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/competitions/${selectedCompetition.id}/register`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || (isEditingRegistration ? 'Failed to update registration' : 'Failed to register'));
      }

      const responseData = await response.json();
      
      if (responseData.total_fee > 0 && responseData.payment_url) {
        // Use replace to prevent adding HitPay URL to browser history
        // This helps prevent back button issues after payment
        window.location.replace(responseData.payment_url);
      } else if (responseData.total_fee > 0) {
        alert(`${isEditingRegistration ? 'Update' : 'Registration'} successful! Total fee: $${responseData.total_fee}. Payment URL not provided.`);
      } else {
        alert(isEditingRegistration ? 'Registration updated successfully!' : 'Registration successful!');
        setShowRegistrationModal(false);
        setIsEditingRegistration(false);
        fetchCompetitions();
        fetchMyRegistrations();
      }
    } catch (error) {
      console.error('Error registering:', error);
      alert(`${isEditingRegistration ? 'Update' : 'Registration'} failed: ${error.message}`);
    }
  }, [currentUser, selectedCompetition, fetchCompetitions, fetchMyRegistrations, isEditingRegistration]);

  const handleCloseModal = useCallback(() => {
    setShowRegistrationModal(false);
    setSelectedCompetition(null);
    setIsEditingRegistration(false);
  }, []);

  const handleGenderUpdate = useCallback(async (selectedGender) => {
    try {
      console.log('Updating user gender to:', selectedGender);
      
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gender: selectedGender
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update gender');
      }

      // Update local state
      setUserGender(selectedGender);
      console.log('Gender updated successfully to:', selectedGender);
      
    } catch (error) {
      console.error('Error updating gender:', error);
      alert('Failed to update gender. Please try again.');
    }
  }, [currentUser]);

  const handleSaveSeedAsSb = useCallback(async (eventCode, value) => {
    try {
      const token = await currentUser.getIdToken();
      const updatedSeasonBests = { ...profileSeasonBests, [eventCode]: value };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          competition_profile: {
            personal_bests: profilePersonalBests,
            season_bests: updatedSeasonBests
          }
        })
      });
      if (!response.ok) throw new Error('Failed to save SB');
      setProfileSeasonBests(updatedSeasonBests);
      alert(`Saved ${value} as your Season Best for ${eventCode}.`);
    } catch (error) {
      console.error('Error saving SB:', error);
      alert('Failed to save Season Best. Please try again.');
    }
  }, [currentUser, profileSeasonBests, profilePersonalBests]);

  // Effects
  useEffect(() => {
    fetchCompetitions();
    fetchUserProfile();
  }, [fetchCompetitions, fetchUserProfile]);

  useEffect(() => {
    fetchMyRegistrations();
  }, [fetchMyRegistrations]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-100">
        {/* Hero Section */}
        <section className="relative py-16 bg-gradient-to-br from-gray-900 via-secondary-900 to-primary-900">
          <div className="absolute inset-0 bg-black opacity-50"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Available Competitions
              </h1>
            </div>
          </div>
        </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Competitions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {competitions.map((competition) => (
                <CompetitionCard
                  key={competition.id}
                  competition={competition}
                  onRegister={handleRegister}
                  existingRegistration={myRegistrations[competition.id]}
                />
              ))}
              
              {competitions.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No competitions available</h3>
                  <p className="mt-1 text-sm text-gray-500">Check back later for upcoming competitions.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Registration Modal */}
      {showRegistrationModal && selectedCompetition && (
        <RegistrationModal
          competition={selectedCompetition}
          registrationData={registrationData}
          onDataChange={setRegistrationData}
          onSubmit={handleSubmitRegistration}
          onClose={handleCloseModal}
          userGender={userGender}
          onGenderUpdate={handleGenderUpdate}
          profileSeasonBests={profileSeasonBests}
          profilePersonalBests={profilePersonalBests}
          onSaveSeedAsSb={handleSaveSeedAsSb}
          isEditing={isEditingRegistration}
        />
      )}
    </>
  );
};

export default MemberCompetitions;